import { DecimalScale, UserPermission } from '@globaleris/ui.library';
import {
  AllocationRequest,
  EnumBloqueoCapacidad,
  EnumOperationState,
  EnumTipoOperacion,
  Operacion,
  OperationRequest,
  RepartirModelRequest,
  Reparto
} from '../../api';
import { RepartirModelResponse } from '../../api/Pioneer/model/RepartirModelResponse';
import {
  ReplicateAllocationModel,
  ReplicateModelRequest
} from '../../api/Pioneer/model/ReplicateModelRequest';
import { enumKeyToValue, enumValueToKey, toLocaleDate } from '../../helpers';
import {
  calculatePercentage,
  formatLocaleNumber,
  formatLocalePercentage
} from '../../helpers/numberHelper';
import { hasPermission } from '../../helpers/permissionsHelper';
import i18n from '../../i18n';
import { NO_DATA } from '../../model/constants';
import { Resources } from '../../model/RBAC/constants';
import {
  AllocationModel,
  DistributedAllocationModel,
  OperationModel
} from './allocationsEdit.model';
import {
  getAllocationActionsConfiguration,
  getAllocationSecondaryActionsConfiguration
} from './allocationsEdit.service';

export const operacionFromApi = (item: Operacion): OperationModel => {
  return (
    item && {
      id: item.id,
      type: enumValueToKey(EnumTipoOperacion, item.tipoOperacion),
      typeDescription: i18n.t(`enums:EnumTipoOperacion.${item.tipoOperacion}`),
      state: enumKeyToValue(EnumOperationState, item.estadoOperacion),
      stateDescription: item.estadoOperacion
        ? i18n.t(`enums:EnumOperationState.${item.estadoOperacion}`)
        : NO_DATA,
      operationDate: item.fechaOperacion,
      operationDateFormatted: toLocaleDate(item.fechaOperacion),
      quantity: item.cantidad,
      quantityFormatted: item.cantidad
        ? formatLocaleNumber(item.cantidad, DecimalScale.ENERGY)
        : NO_DATA,
      sendQuantity: item.cantidadEnvio,
      TSOQuantity: item.cantidadTSO,
      price: item.precio,
      priceFormatted: item.precio
        ? formatLocaleNumber(item.precio, DecimalScale.CURRENCY)
        : NO_DATA,
      logisticElementDescription: item.elementoLogistico.descripcion ?? NO_DATA,
      allocations: repartoArrayFromApi(item.repartos)
    }
  );
};

export const repartoArrayFromApi = (items: Reparto[]): AllocationModel[] => {
  return items && items.map(repartoFromApi);
};

export const repartoFromApi = (item: Reparto): AllocationModel => {
  return (
    item && {
      id: item.id,
      quantity: item.cantidad,
      capacity: item.productoCapacidad?.qdContratada,
      sendQuantity: item.cantidadEnvio,
      TSOQuantity: item.cantidadTSO,
      productCapacityId: item.productoCapacidad?.id,
      productCapacityCode: item.productoCapacidad?.codProductoCapacidad,
      productCapacityType: item.productoCapacidad?.tipoProductoCapacidad,
      productCapacityNatureType: item.productoCapacidad?.tipoNaturaleza,
      productQdHired: item.productoCapacidad?.qdContratada,
      bundledParentProduct: item.productoCapacidad?.productoPadre,
      locked: enumValueToKey(EnumBloqueoCapacidad, item.productoCapacidad?.bloqueo),
      usePercentage: calculatePercentage(item.cantidad! / item.productoCapacidad?.qdContratada!),
      allocatedQuantityBreakdown: `${formatLocaleNumber(
        item.cantidad,
        DecimalScale.ENERGY
      )} (${formatLocalePercentage(item.cantidad! / item.productoCapacidad?.qdContratada!)}%)`,
      productCapacityBreakdown: `${item.productoCapacidad?.codProductoCapacidad} (${item.productoCapacidad?.tipoProductoCapacidad} - ${item.productoCapacidad?.tipoNaturaleza})`,
      TSOInformationBreakdown: `${formatLocaleNumber(
        item.cantidadTSO!,
        DecimalScale.ENERGY
      )} (${formatLocalePercentage(item.cantidadTSO! / item.productoCapacidad?.qdContratada!)}%)`,
      lastSendInformationBreakdown: `${formatLocaleNumber(
        item.cantidadEnvio!,
        DecimalScale.ENERGY
      )} (${formatLocalePercentage(item.cantidadEnvio! / item.productoCapacidad?.qdContratada!)}%)`,
      isModified: false,
      actionsConfiguration: getAllocationActionsConfiguration(
        hasPermission(Resources.Nomination, [UserPermission.EDIT])
      ),
      secondaryActionsConfiguration: getAllocationSecondaryActionsConfiguration()
    }
  );
};

export const operacionToApiRequestPatch = (item: OperationModel): OperationRequest => {
  return (
    item && {
      id: item.id,
      tipoOperacion: enumKeyToValue(EnumTipoOperacion, item.type),
      estadoOperacion: enumKeyToValue(EnumOperationState, item.state),
      cantidad: item.quantity
    }
  );
};

export const allocationToApiRequestPatch = (item: AllocationModel): AllocationRequest => {
  return (
    item && {
      id: item.id,
      cantidad: item.quantity
    }
  );
};

export const allocationsToApiDistributeAllocationsRequest = (
  items: AllocationModel[]
): RepartirModelRequest[] => {
  return (
    items &&
    items.map(item => {
      return {
        id: item.id,
        cantidad: item.quantity!,
        porcentaje: item.usePercentage!,
        bloqueo: enumValueToKey(EnumBloqueoCapacidad, item.locked!)
      };
    })
  );
};

export const allocationsFromApiRepartirModelResponse = (
  items: RepartirModelResponse[]
): DistributedAllocationModel[] => {
  return (
    items &&
    items.map(item => {
      return {
        id: item.id,
        quantity: item.cantidad,
        usePercentage: item.porcentaje
      };
    })
  );
};

export const allocationsToReplicateAllocationRequest = (
  items: AllocationModel[]
): ReplicateAllocationModel[] => {
  return (
    items &&
    items.map(item => {
      return {
        allocationId: item.id,
        allocationQuantity: item.quantity!
      };
    })
  );
};

export const replicateAllocationsRequest = (
  allocationsRequest: ReplicateAllocationModel[],
  allocationsModifiedRequest: ReplicateAllocationModel[],
  initDate: Date,
  endDate: Date
): ReplicateModelRequest => {
  return {
    allocations: allocationsRequest,
    allocationsModified: allocationsModifiedRequest,
    initDate: initDate,
    endDate: endDate
  };
};

export const allocationsFromApiReplicateModelResponse = (
  items: any
): DistributedAllocationModel[] => {
  return (
    items &&
    items.map(item => {
      return {
        id: item.id,
        quantity: item.cantidad,
        usePercentage: item.porcentaje,
        lock: enumKeyToValue(EnumBloqueoCapacidad, item.bloqueo)
      };
    })
  );
};